import { getInitialAbTestValue } from '~/utils/optimizely/ab-test'
import { waitForOptimizelySdk, OPTIMIZELY_MAX_WAIT_MS } from '~/utils/optimizely/sdk-ready'

/**
 * CC-40297 — the Travel Insurance mandatory email A/B test.
 *
 * 0 = control (email optional, today's form), 1 = variant (a valid email is
 * required before quote results).
 *
 * Same surfaces as the other custom-JS tests: the Optimizely variation sets
 * `window.ABTestTravelMandatoryEmail`, mirrored to `abtest.travelMandatoryEmail`
 * in localStorage, plus `?travelMandatoryEmail=0|1` for QA. No experiment ID is
 * looked up, so the same code works against every Optimizely project.
 *
 * Deliberately NOT `resolveAbTestValue`: that helper keeps a stored value
 * forever and persists a guessed `0` when the global is missing. Here the
 * window global always wins over storage, and we never persist a guess while
 * the variation JS is still running after a client-side route change.
 */

export const TRAVEL_MANDATORY_EMAIL_WINDOW_KEY = 'ABTestTravelMandatoryEmail'
export const TRAVEL_MANDATORY_EMAIL_STORAGE_KEY = 'abtest.travelMandatoryEmail'
export const TRAVEL_MANDATORY_EMAIL_FORCE_PARAM = 'travelMandatoryEmail'

// The variation JS can still be running when the widget mounts, especially
// after an in-app navigation.
export const TRAVEL_MANDATORY_EMAIL_GRACE_MS = 1000

const POLL_INTERVAL_MS = 50

type Arm = 0 | 1

interface ResolveOptions {
  query?: Record<string, unknown>
  maxWaitMs?: number
  graceMs?: number
}

function toArm (value: unknown): Arm {
  return Number(value) === 1 ? 1 : 0
}

function readWindowArm (): unknown {
  return (window as unknown as Record<string, unknown>)[TRAVEL_MANDATORY_EMAIL_WINDOW_KEY]
}

function isSet (value: unknown): boolean {
  return value !== null && value !== undefined && value !== ''
}

export function readTravelMandatoryEmailForceParam (query: Record<string, unknown> = {}): Arm | null {
  const raw = query?.[TRAVEL_MANDATORY_EMAIL_FORCE_PARAM]
  const value = Array.isArray(raw) ? raw[0] : raw

  if (value === '1' || value === 1) {
    return 1
  }

  if (value === '0' || value === 0) {
    return 0
  }

  return null
}

async function waitForWindowArm (graceMs: number): Promise<unknown> {
  const start = Date.now()

  while (!isSet(readWindowArm()) && Date.now() - start < graceMs) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS))
  }

  return readWindowArm()
}

export async function resolveTravelMandatoryEmailArm ({
  query = {},
  maxWaitMs = OPTIMIZELY_MAX_WAIT_MS,
  graceMs = TRAVEL_MANDATORY_EMAIL_GRACE_MS
}: ResolveOptions = {}): Promise<Arm> {
  if (!import.meta.client) {
    return 0
  }

  const forced = readTravelMandatoryEmailForceParam(query)
  if (forced !== null) {
    setLocal(TRAVEL_MANDATORY_EMAIL_STORAGE_KEY, forced)
    return forced
  }

  // Optimizely blocked or slow: fail open to control, keep any real assignment
  if (!(await waitForOptimizelySdk(maxWaitMs))) {
    return 0
  }

  // The window global is the source of truth and overrides any stored arm
  const windowArm = await waitForWindowArm(graceMs)
  if (isSet(windowArm)) {
    const arm = toArm(windowArm)
    setLocal(TRAVEL_MANDATORY_EMAIL_STORAGE_KEY, arm)
    return arm
  }

  return toArm(getLocal(TRAVEL_MANDATORY_EMAIL_STORAGE_KEY))
}

export function useTravelMandatoryEmailExperiment () {
  const route = useRoute()
  // Starts as Control to match the SSR render; reading storage here would
  // cause a hydration mismatch for returning Variant visitors.
  const isMandatoryEmail = ref(false)

  async function resolve (): Promise<Arm> {
    // Apply the stored arm right after mount, before Optimizely answers
    isMandatoryEmail.value = getInitialAbTestValue(TRAVEL_MANDATORY_EMAIL_STORAGE_KEY, { numeric: true }) === 1

    const arm = await resolveTravelMandatoryEmailArm({ query: route.query })
    isMandatoryEmail.value = arm === 1
    return arm
  }

  return {
    isMandatoryEmail,
    resolve
  }
}
